import {
  useEffect, useRef, useCallback, useMemo,
} from 'react';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import roundAmountWithRules from './roundAmountWithRules';

function useDeepCompareMemoize(value) {
  const ref = useRef();

  if (!isEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepEffect(callback, dependencies) {
  useEffect(callback, dependencies.map(useDeepCompareMemoize));
}

function useDebouncedEffect(callback, dependencies, delay) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      callback();
    }, delay);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [ ...dependencies, delay ]);
}

function useCombineErrorCallback(errorCallback, i18n) {
  return useCallback(
    service => params => service({ errorCallback, i18n, ...params }),
    [ errorCallback, i18n ],
  );
}

function usePennyRounding(dependencies) {
  const [ yourPayment, currentActivePaymentMethod, pennyRounding, currencyCode ] = dependencies;

  return useMemo(() => {
    if (currentActivePaymentMethod === 'CASH' && !isEmpty(pennyRounding)) {
      try {
        return roundAmountWithRules(yourPayment, pennyRounding, currencyCode);
      } catch (error) {
        return {};
      }
    }

    return {};
  }, dependencies);
}

export {
  useDeepEffect,
  useDebouncedEffect,
  useCombineErrorCallback,
  usePennyRounding,
};
